import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';

// The one form control in the app.
//
// Every settings panel builds itself out of these, which is why restyling this
// file restyles all of them. It renders three shapes: a labelled select, an
// on/off switch, and a labelled text field.
const FormInput = ({ name, labelText, value, inputType, maxLen, onValueChange, options, isValid, disabled, hint }) => {

    const [fieldData, setFieldData] = useState(value);

    // Kept in step with the prop.
    //
    // useState only reads its argument once, so a value changed anywhere else
    // left this control showing the old one -- which the recitation button on
    // the home screen would do to the recitation switch in the Sound panel
    // every time it was pressed.
    useEffect(() => { setFieldData(value); }, [value]);

    let selectedItem = '';
    if (options) {
        const match = options.filter((option) => `${option.id}` === `${fieldData}`);
        try {
            selectedItem = match[0].id;
        } catch (error) {
            // No match: the select falls back to its first entry, as before.
        }
    }

    const dataChanged = (v) => {
        setFieldData(v);
        onValueChange(v);
    };

    if (inputType === 'select') {
        return (
            <div className={'field' + (isValid === false ? ' field--invalid' : '')}>
                {labelText !== '' && <label className="field__label" htmlFor={name}>{labelText}</label>}
                <select
                    id={name}
                    name={name}
                    value={selectedItem}
                    disabled={disabled}
                    onChange={(e) => dataChanged(e.currentTarget.value)}
                >
                    {options.map((e, key) => (
                        <option key={key} value={e.id}>{e.description}</option>
                    ))}
                </select>
            </div>
        );
    }

    if (inputType === 'checkbox') {
        // A button with role="switch" rather than a checkbox: it is the control
        // this actually is, it carries its own state to a screen reader, and it
        // needs none of the label-for-input contortion a styled checkbox does.
        const on = fieldData === true || fieldData === 'true';
        return (
            <div className="switch">
                <span className="switch__text" id={`${name}-label`}>
                    {labelText}
                    {hint && <small>{hint}</small>}
                </span>
                <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    aria-labelledby={`${name}-label`}
                    id={name}
                    className="knob"
                    disabled={disabled}
                    onClick={() => dataChanged(!on)}
                />
            </div>
        );
    }

    return (
        <div className={'field' + (isValid === false ? ' field--invalid' : '')}>
            {labelText !== '' && <label className="field__label" htmlFor={name}>{labelText}</label>}
            <input
                type={inputType}
                name={name}
                id={name}
                placeholder={hint || ''}
                value={fieldData ?? ''}
                maxLength={maxLen}
                disabled={disabled}
                onChange={(e) => dataChanged(e.currentTarget.value)}
            />
        </div>
    );
};

FormInput.defaultProps = {
    labelText: '',
    value: '',
    inputType: 'text',
    options: [{ id: '', description: '' }],
    maxLen: 100,
    disabled: false,
};

FormInput.propTypes = {
    name: PropTypes.string.isRequired,
    labelText: PropTypes.string,
    value: PropTypes.any,
    inputType: PropTypes.string.isRequired,
    maxLen: PropTypes.number,
    options: PropTypes.array,
    onValueChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    isValid: PropTypes.bool,
    hint: PropTypes.string,
};

export default FormInput;
